import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Youtube } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MaskedLine, FadeUp, Overline, Stagger, StaggerItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { VideoCard } from "@/components/site/VideoCard";
import { EditorialMarquee } from "@/components/site/Marquee";
import { SocialIconButtons } from "@/components/site/SocialIcons";
import { IMAGES, MANIFESTO, PACKAGES, YOUTUBE_URL } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const INTRO_MS = 2300;

/* ---------------- Latest Golf News (homepage) ---------------- */
const fmtShort = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const BreakingBanner = () => {
  const { data } = useQuery({
    queryKey: ["home-breaking"],
    queryFn: () => api.get("/news/breaking").then((r) => r.data.breaking),
    refetchInterval: 60000,
  });
  if (!data) return null;
  return (
    <a
      href={data.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="home-breaking-banner"
      className="group relative z-30 mt-[72px] flex items-center gap-3 border-b border-brand-accent/30 bg-brand-accent/10 px-5 py-3 md:px-8"
    >
      <span className="shrink-0 bg-brand-accent px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink">Breaking</span>
      <span className="truncate font-display text-sm font-bold uppercase tracking-tight text-white group-hover:text-brand-accent">
        {data.headline}
      </span>
      <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-brand-accent" />
    </a>
  );
};

const LatestGolfNews = () => {
  const { data } = useQuery({
    queryKey: ["home-news"],
    queryFn: () => api.get("/news/items", { params: { limit: 5 } }).then((r) => r.data.items),
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const { data: originals } = useQuery({
    queryKey: ["home-originals"],
    queryFn: () => api.get("/news/originals").then((r) => r.data.originals),
    staleTime: 60000,
  });
  const featuredOriginal = (originals || [])[0] || null;
  const wire = data || [];
  if (wire.length === 0 && !featuredOriginal) return null;
  return (
    <section className="border-t border-white/10 bg-ink-2" data-testid="home-news">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>FORE Golf News</Overline>
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl lg:text-5xl" data-testid="home-news-heading">
              Latest Golf News
            </h2>
          </div>
          <Link
            to="/news"
            data-testid="home-news-view-all"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-brand-accent"
          >
            View All News
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>

        {featuredOriginal && (
          <FadeUp delay={0.05}>
            <Link
              to={`/news/${featuredOriginal.slug}`}
              data-testid={`home-original-${featuredOriginal.id}`}
              className="group mt-10 grid gap-6 border border-white/15 bg-ink p-7 transition-colors duration-300 hover:border-brand-accent sm:p-9 md:grid-cols-[1fr_auto] md:items-center"
            >
              <div>
                <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                  <span className="bg-white px-2.5 py-1 font-bold text-ink">FORE Original</span>
                  {featuredOriginal.series && <span className="border border-brand-accent px-2.5 py-1 font-bold text-brand-accent">{featuredOriginal.series}</span>}
                </div>
                <h3 className="mt-4 max-w-3xl font-display text-2xl font-extrabold leading-tight tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent sm:text-3xl" data-testid="home-original-title">
                  {featuredOriginal.title}
                </h3>
                {featuredOriginal.subtitle && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">{featuredOriginal.subtitle}</p>}
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {featuredOriginal.author} • {fmtShort(featuredOriginal.published_at)}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-brand-accent">
                Read Article
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </FadeUp>
        )}

        {wire.length > 0 && (
          <FadeUp delay={0.1}>
            {featuredOriginal && (
              <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40" data-testid="home-wire-label">From the FORE News Wire</p>
            )}
            <div className={`${featuredOriginal ? "mt-4" : "mt-10"} divide-y divide-white/10 border-y border-white/10`}>
              {wire.map((item) => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`home-news-${item.id}`}
                  className="group grid gap-1 py-5 sm:grid-cols-[160px_1fr_auto] sm:items-center sm:gap-6"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-accent">{item.source}</span>
                  <span className="font-display text-lg font-bold uppercase leading-tight tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent">
                    {item.headline}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{fmtShort(item.published_at)}</span>
                </a>
              ))}
            </div>
          </FadeUp>
        )}
      </div>
    </section>
  );
};

const IntroOverlay = () => (
  <motion.div
    className="fixed inset-0 z-[90] grid place-items-center bg-ink"
    initial={{ opacity: 1 }}
    exit={{ y: "-100%", transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] } }}
    data-testid="intro-overlay"
  >
    <div className="flex flex-col items-center">
      <motion.img
        src="/logo-512.png"
        alt="Fore The Hell Of It"
        className="h-44 w-auto object-contain drop-shadow-[0_0_60px_rgba(15,81,50,0.55)] md:h-64"
        initial={{ opacity: 0, scale: 0.82, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, ease: EASE }}
        data-testid="intro-logo"
      />
      <motion.div
        className="mt-8 h-px bg-brand-accent"
        initial={{ width: 0 }}
        animate={{ width: 140 }}
        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
      />
      <motion.p
        className="mt-5 font-mono text-[11px] uppercase tracking-[0.4em] text-white/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
      >
        Great Guests · Questionable Golf · Real Conversations
      </motion.p>
    </div>
  </motion.div>
);

const Hero = ({ delayOffset = 0 }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  const lines = [
    { text: "Great Guests.", accent: false },
    { text: "Questionable Golf.", accent: true },
    { text: "Real Conversations.", accent: false },
  ];

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-y-0 right-0 flex items-center">
        <img
          src="/hero-flag.jpg"
          alt="Fore The Hell Of It flag with a golf ball"
          loading="eager"
          fetchPriority="high"
          className="h-full w-auto object-contain opacity-40 md:opacity-100"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-transparent md:via-ink/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-36 md:px-8 md:pb-24">
        <div>
          <div>
            <MaskedLine delay={0.15 + delayOffset}>
              <Overline>Fore The Hell Of It — A Golf Media Brand</Overline>
            </MaskedLine>

            <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              {lines.map((l, i) => (
                <MaskedLine key={l.text} delay={0.3 + delayOffset + i * 0.14}>
                  <span className={l.accent ? "text-[0.9em] text-brand-accent" : "text-white"}>{l.text}</span>
                </MaskedLine>
              ))}
            </h1>

            <MaskedLine delay={0.95 + delayOffset}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-white/80 md:text-lg" data-testid="hero-subheadline">
                Carlos and Joe hit the course with athletes, comedians, entrepreneurs, creators,
                first responders, and interesting people from all walks of life. 18 holes.
                Real conversations. Plenty of questionable golf.
              </p>
            </MaskedLine>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15 + delayOffset, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-youtube"
                className="group flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
              >
                Watch Fore
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/guests#apply"
                data-testid="hero-cta-guest"
                className="group flex items-center gap-3 border border-white/30 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
              >
                Play a Round With Us
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 + delayOffset, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <ChevronDown className="h-5 w-5 animate-bounce text-white/50" />
      </motion.div>
    </section>
  );
};

const ManifestoSection = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="manifesto-section">
    <div className="grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHeading
            overline="This Is Fore"
            title={<>The people are<br />the story.</>}
            copy="Golf is the setting. Every round gives us a few hours with someone worth getting to know — no studio, no desk, no script."
            testid="manifesto-heading"
          />
          <FadeUp delay={0.2} className="mt-10">
            <Link
              to="/about"
              data-testid="manifesto-about-link"
              className="group inline-flex items-center gap-3 border-b border-brand-accent pb-1 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:text-brand-accent"
            >
              Read Our Story
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </div>

      <div className="lg:col-span-7">
        <Stagger className="flex flex-col">
          {MANIFESTO.map((m) => (
            <StaggerItem key={m.n}>
              <div
                className="group grid grid-cols-[auto_1fr] gap-6 border-t border-white/10 py-10 transition-colors duration-500 hover:border-brand md:gap-12"
                data-testid={`manifesto-chapter-${m.n}`}
              >
                <span className="font-mono text-sm text-brand-accent transition-transform duration-500 group-hover:-translate-y-1 md:text-base">
                  {m.n}
                </span>
                <div>
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent md:text-4xl">
                    {m.title}
                  </h3>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted md:text-base">{m.body}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

/* ---------------- Latest Fore Short (homepage feature) ---------------- */
const LatestShortSection = () => {
  const { data } = useQuery({
    queryKey: ["home-short"],
    queryFn: () => api.get("/youtube/latest").then((r) => r.data),
    staleTime: 300000,
  });
  const short = (data?.shorts || [])[0];
  if (!short) return null;
  return (
    <section className="border-t border-white/10 bg-ink-2" data-testid="home-short-section">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <FadeUp>
          <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center md:gap-12">
            <div className="w-44 shrink-0 sm:w-52">
              <VideoCard video={short} vertical testid="home-short-card" />
            </div>
            <div>
              <Overline>Fresh Off The Channel</Overline>
              <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-4xl" data-testid="home-short-heading">
                Latest <span className="text-brand-accent">Fore Short.</span>
              </h2>
              <p className="mt-4 max-w-xl font-display text-lg font-bold uppercase leading-tight tracking-tight text-white/85" data-testid="home-short-title">
                {short.title}
              </p>
              {short.views && (
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted" data-testid="home-short-views">{short.views}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={short.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-short-watch"
                  className="group inline-flex items-center gap-3 bg-brand-accent px-7 py-3.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white"
                >
                  <Youtube className="h-4 w-4" />
                  Watch the Short
                </a>
                <Link
                  to="/videos"
                  data-testid="home-short-more"
                  className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-brand-accent"
                >
                  All Fore Videos
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const FollowAlongSection = () => (
  <section className="border-t border-white/10" data-testid="follow-along-section">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <FadeUp>
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div>
            <Overline>Follow Along</Overline>
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl" data-testid="follow-along-heading">
              New episodes drop<br />on <span className="text-brand-accent">YouTube.</span>
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
              Guest rounds, Fore Shorts, challenges, and road trips — subscribe so the first
              drop doesn't sneak past you.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-4">
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="follow-along-subscribe"
              className="group inline-flex items-center gap-3 bg-brand-accent px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white"
            >
              <Youtube className="h-4 w-4" />
              Subscribe on YouTube
            </a>
            <SocialIconButtons exclude={["youtube"]} testidPrefix="follow-along" />
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const GuestsPreviewSection = () => (
  <section className="border-t border-white/10 bg-ink-2" data-testid="guests-preview-section">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
      <FadeUp>
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div>
            <Overline>Play a Round With Us</Overline>
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl" data-testid="guests-preview-heading">
              Got a story worth<br />a <span className="text-brand-accent">round?</span>
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
              Athletes. Comedians. Entrepreneurs. Creators. First responders. And people with
              stories worth hearing. 18 holes. No studio. No script.
            </p>
          </div>
          <Link
            to="/guests#apply"
            data-testid="guests-preview-apply"
            className="group inline-flex shrink-0 items-center gap-3 bg-white px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
          >
            Interested in Playing a Round?
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </FadeUp>
    </div>
  </section>
);

const SponsorStrip = () => (
  <section className="border-t border-white/10" data-testid="sponsor-strip">
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <FadeUp>
          <Overline>Partnerships</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-4xl" data-testid="sponsor-strip-heading">
            Sponsor the show.
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Link
            to="/sponsor"
            data-testid="sponsor-strip-all"
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-brand-accent"
          >
            All Sponsorship Options
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>

      <Stagger className="mt-12 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
        {PACKAGES.map((p) => (
          <StaggerItem key={p.name}>
            <Link
              to="/sponsor"
              data-testid={`sponsor-strip-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`group relative flex h-full flex-col bg-ink p-7 transition-colors duration-500 hover:bg-ink-3 md:p-9 ${p.featured ? "shadow-[inset_0_0_0_1px_rgba(204,255,0,0.35)]" : ""}`}
            >
              {p.featured && (
                <span className="absolute right-6 top-6 bg-brand-accent px-2.5 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.2em] text-ink">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent">
                {p.name}
              </h3>
              <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-accent">{p.price}</p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted">{p.items[0]}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 transition-colors duration-300 group-hover:text-white">
                Start Here
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="relative overflow-hidden border-t border-white/10" data-testid="cta-band">
    <img src={IMAGES.travel} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 contrast-125 saturate-[0.7]" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
    <div className="relative mx-auto max-w-7xl px-5 py-28 md:px-8 md:py-40">
      <FadeUp>
        <Overline>Work With Us</Overline>
        <h2 className="mt-4 max-w-4xl font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="cta-band-heading">
          Put your brand <span className="text-brand-accent">in play.</span>
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
          Reach a community of golfers, founders, and competitors who actually watch.
          Sponsorships, integrations, and custom series.
        </p>
        <Link
          to="/sponsor"
          data-testid="cta-band-button"
          className="group mt-10 inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-accent hover:text-ink"
        >
          Pick Your Starting Tee
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  const reduce = useReducedMotion();
  const [delayOffset] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      return sessionStorage.getItem("fthoi_intro") ? 0 : 2.3;
    } catch {
      return 0;
    }
  });
  const [showIntro, setShowIntro] = useState(delayOffset > 0 && !reduce);

  useEffect(() => {
    if (!showIntro) return;
    const t = setTimeout(() => {
      setShowIntro(false);
      try {
        sessionStorage.setItem("fthoi_intro", "1");
      } catch {}
    }, INTRO_MS);
    return () => clearTimeout(t);
  }, [showIntro]);

  return (
    <main data-testid="home-page">
      <AnimatePresence>{showIntro && <IntroOverlay key="intro" />}</AnimatePresence>
      <BreakingBanner />
      <Hero delayOffset={showIntro || delayOffset > 0 ? delayOffset : 0} />
      <EditorialMarquee />
      <LatestShortSection />
      <ManifestoSection />
      <FollowAlongSection />
      <GuestsPreviewSection />
      <SponsorStrip />
      <LatestGolfNews />
      <CtaBand />
    </main>
  );
}
